import React, { useState, useEffect } from "react";

/**
 * StepDetails — Collapsible agent reasoning panel shown inside done cards.
 *
 * Two real data sources — no hardcoded strings:
 *   reasoning  → model chain-of-thought bullets (why the step ran, what it did, what data it used)
 *   data       → actual extracted key-value pairs (platforms, fields, IDs, etc.)
 *
 * Data is deduplicated by (label, value) pair before rendering.
 */
const StepDetails = ({ details }) => {
  if (!details) return null;

  const reasoning = (details.reasoning || []).filter(Boolean).slice(0, 6);

  // Deduplicate data by (label, value) — prevents repeated chips from multi-turn accumulation
  const seenPairs = new Set();
  const data = (details.data || []).filter(d => {
    if (!d || !d.label || !d.value) return false;
    const key = `${d.label}::${d.value}`;
    if (seenPairs.has(key)) return false;
    seenPairs.add(key);
    return true;
  });

  const table = details.table || null;

  if (reasoning.length === 0 && data.length === 0 && !table) return null;

  return (
    <div className="mt-2 pt-2 border-t border-gray-100 space-y-2.5">

      {/* Agent reasoning — why this step was needed, actions taken, data used */}
      {reasoning.length > 0 && (
        <ul className="space-y-1.5">
          {reasoning.map((step, i) => (
            <li key={i} className="flex items-start gap-2 text-[0.82rem] text-gray-900 leading-relaxed">
              <span className="mt-[5px] shrink-0 text-[0.45rem] text-gray-400">●</span>
              <span className="break-words">{step}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Structured table — credentials / mappings / build summary */}
      {table && table.headers && table.rows && table.rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 text-[0.75rem]">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-blue-50">
                {table.headers.map((h, i) => (
                  <th
                    key={i}
                    className="px-3 py-1.5 text-left text-[0.72rem] font-bold text-blue-700 border-b border-gray-200 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? "bg-white" : "bg-gray-50/60"}>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className="px-3 py-1.5 text-gray-800 font-medium border-b border-gray-100 last:border-b-0 align-top"
                    >
                      {cell || "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Fallback data pills — only shown when there is no table */}
      {!table && data.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {data.map((item, i) => (
            <span key={i} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5 text-[0.7rem] select-none">
              <span className="text-blue-400 font-medium">{item.label}:</span>
              <span className="text-blue-700 font-semibold">{item.value}</span>
            </span>
          ))}
        </div>
      )}

    </div>
  );
};

/**
 * PhaseCard — A single step card in the integration timeline.
 * Matches the app's light theme (blue-600 primary, gray backgrounds).
 *
 * Props:
 *   stepNumber: number
 *   category: string (e.g., "SOURCE SETUP")
 *   title: string (e.g., "Configure HubSpot as Data Source")
 *   icon: string (emoji)
 *   tags: string[] (e.g., ["HubSpot CRM", "OAuth 2.0"])
 *   status: "done" | "current" | "pending"
 *   details: object | null (reasoning/actions/data — shown collapsed, user can expand)
 */
const PhaseCard = ({ stepNumber, showStepNumber = false, category, title, icon, tags = [], status = "pending", onProceed, proceedLabel, expandableContent, defaultExpanded = false, details = null }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  useEffect(() => {
    setIsExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const statusStyles = {
    done: {
      badge: "bg-emerald-500 text-white",
      border: "border-blue-200",
      bg: "bg-blue-50/40",
      category: "text-blue-600",
      title: "text-gray-900",
      tagBg: "bg-blue-100 text-blue-700 border-blue-200",
    },
    current: {
      badge: "bg-blue-600 text-white",
      border: "border-blue-200",
      bg: "bg-blue-50/40",
      category: "text-blue-600",
      title: "text-gray-900",
      tagBg: "bg-blue-100 text-blue-700 border-blue-200",
    },
    pending: {
      badge: "bg-blue-100 text-blue-400",
      border: "border-blue-100",
      bg: "bg-blue-50/20",
      category: "text-blue-400",
      title: "text-gray-500",
      tagBg: "bg-blue-50 text-blue-400 border-blue-100",
    },
  };

  const s = statusStyles[status] || statusStyles.pending;

  // Whether this card has any expandable content (details OR rich expandableContent)
  // Show reasoning for both "done" and "current" cards — not for pending
  const hasDetails = details && (status === "done" || status === "current");
  const isExpandable = (expandableContent || hasDetails) && status !== "pending";

  return (
    <div
      className={`rounded-xl border ${s.border} ${s.bg} px-4 py-2 transition-all duration-200 ${
        isExpandable ? "cursor-pointer hover:shadow-md" : (status !== "pending" ? "" : "opacity-60")
      }`}
      onClick={() => isExpandable && setIsExpanded(!isExpanded)}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Step number badge — only shown in final PhaseTimeline, not mid-conversation */}
          {showStepNumber && (
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-black bg-blue-600 text-white shrink-0">
              {String(stepNumber).padStart(2, "0")}
            </div>
          )}

          <div>
            <p className={`text-[0.6rem] font-black uppercase tracking-[0.15em] ${s.category}`}>
              {category}
            </p>
            <p className={`text-[0.82rem] font-bold leading-tight ${s.title}`}>
              {icon} {title}
            </p>
          </div>
        </div>

        {/* Proceed button */}
        <div className="flex items-center gap-1.5">
          {/* Inline proceed button — show on done or current, not pending */}
          {onProceed && status !== "pending" && (
            <button
              onClick={(e) => { e.stopPropagation(); onProceed(); }}
              className="flex items-center gap-1.5 ml-1 px-3 py-1.5 bg-blue-600 text-white text-[0.72rem] font-bold rounded-lg hover:bg-blue-700 transition active:scale-[0.97] shadow-sm"
            >
              {proceedLabel || "Next Step"}
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          {/* Chevron — shown whenever the card is expandable (has details or expandableContent) */}
          {isExpandable && (
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ml-1 ${isExpanded ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </div>
      </div>

      {/* Step details — agent reasoning — collapsed by default, expands on click */}
      {hasDetails && isExpanded && (
        <StepDetails details={details} />
      )}

      {/* Expandable content — slides open/closed */}
      {expandableContent && isExpanded && (
        <div className="mt-2 animate-fadeIn" onClick={(e) => e.stopPropagation()}>
          {expandableContent}
        </div>
      )}
    </div>
  );
};

export default PhaseCard;
